import {AnimatePresence, motion} from 'motion/react';
import {useState} from 'react';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {APP_ANIMATIONS} from '@/const';
import {testEmail} from '@/helper';
import EmailIcon from '@/icons/EmailIcon';
import useHandleBackendErrors from '@/hooks/useHandleBackendErrors';
import useSignUp from '@/queries/auth/useSignUp';
import Button, {BTN_TAGS, BTN_TYPES} from '@/ui/Button';
import Checkbox from '@/ui/Checkbox';
import Input from '@/ui/Input';
import Overlay from '@/ui/Overlay';
import RelatedAdsLogoMedia from '@/ui/RelatedAdsLogoMedia';
import {AUTH_PATHNAME} from '../../interface';
import AdditionalAction from '../AdditionalAction';
import ResponseMessage from '../ResponseMessage';
import StepTitle from '../StepTitle';
import styles from './styles.module.scss';

interface IFormValues {
    user_email: string;
    agree: boolean;
}

const SignUpForm = () => {
    const [done, setDone] = useState(false);
    const {mutate, isPending, error} = useSignUp();
    const formMethods = useForm<IFormValues>({values: {user_email: '', agree: false}});

    const {
        handleSubmit,
        register,
        setError,
        getValues,
        formState: {errors}
    } = formMethods;

    const onSubmit: SubmitHandler<IFormValues> = data => {
        mutate({data}, {onSuccess: () => setDone(true)});
    };

    const openLogin = () => window.open(AUTH_PATHNAME.login, '_self');

    useHandleBackendErrors({error, setError});

    return (
        <FormProvider {...formMethods}>
            <RelatedAdsLogoMedia />
            <AnimatePresence mode={'wait'}>
                {done ? (
                    <motion.div key={'done'} {...APP_ANIMATIONS.fadeInOut} className={styles.form}>
                        <Overlay visible={isPending} isAbsolutePos={true} />
                        <ResponseMessage
                            message={
                                <>
                                    Thank you for signing up with Related Ads.
                                    <br /> A confirmation email with the next steps will be soon sent to you on this
                                    email address.
                                </>
                            }
                        />
                        <Input Icon={EmailIcon} inputProps={{value: getValues('user_email'), disabled: true}} />
                        <AdditionalAction
                            text={'Not seeing the e-mail?'}
                            btnText={'Resend'}
                            onClick={handleSubmit(onSubmit)}
                        />
                    </motion.div>
                ) : (
                    <motion.form
                        key={'form'}
                        {...APP_ANIMATIONS.fadeInOut}
                        onSubmit={handleSubmit(onSubmit)}
                        className={styles.form}
                        noValidate
                    >
                        <StepTitle title={'Create an account'} />
                        <div className={styles.inputs}>
                            <Input
                                error={errors?.user_email?.message}
                                Icon={EmailIcon}
                                inputProps={{
                                    placeholder: 'E-mail',
                                    type: 'email',
                                    ...register('user_email', {
                                        required: 'E-mail is required',
                                        validate: {
                                            matchPattern: v => testEmail(v) || 'E-mail is invalid'
                                        }
                                    })
                                }}
                            />
                            <Checkbox
                                error={errors?.agree?.message}
                                label={
                                    <div className={styles.agree}>
                                        Agreed to
                                        <Button
                                            ButtonTag={BTN_TAGS.a}
                                            btnType={BTN_TYPES.opacity}
                                            btnStyles={[styles.flatBtn]}
                                            btnProps={{
                                                href: 'https://streetmedia.me/privacy-cookie/',
                                                target: '_blank'
                                            }}
                                        >
                                            Cookie Policy
                                        </Button>
                                        &
                                        <Button
                                            ButtonTag={BTN_TAGS.a}
                                            btnType={BTN_TYPES.opacity}
                                            btnStyles={[styles.flatBtn]}
                                            btnProps={{
                                                href: 'https://streetmedia.me/privacy-policy/',
                                                target: '_blank'
                                            }}
                                        >
                                            Privacy policy
                                        </Button>
                                    </div>
                                }
                                checkboxProps={{...register('agree', {required: 'You must agree to the terms'})}}
                            />
                        </div>
                        <div className={styles.footer}>
                            <Button loading={isPending} btnStyles={[styles.submit]}>
                                Sign up
                            </Button>
                            <AdditionalAction
                                text={'Already have an account?'}
                                btnText={'Log in'}
                                onClick={openLogin}
                            />
                        </div>
                    </motion.form>
                )}
            </AnimatePresence>
            <Button
                btnType={BTN_TYPES.opacity}
                onClick={() => setDone(false)}
                btnStyles={[styles.flatBtn, styles.back, done && styles.showBack]}
            >
                Back
            </Button>
        </FormProvider>
    );
};

export default SignUpForm;
